import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Layers, Mountain, Ruler, Eye, Download } from 'lucide-react';

const BathymetryPage = () => {
  const [layers, setLayers] = useState({
    depth: true,
    temperature: true,
    salinity: false,
  });

  const [vesselData] = useState({
    lat: -23.745,
    lon: -134.120,
    speed: 9.8,
    heading: 212,
  });

  const [viewMode, setViewMode] = useState('3d');
  const [showContours, setShowContours] = useState(true);
  const bathymetryCanvasRef = useRef(null);

  const toggleLayer = (layer) => {
    setLayers(prev => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  };

  useEffect(() => {
    const canvas = bathymetryCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * 2;
    const height = canvas.height = 800;
    ctx.scale(2, 2);

    const centerX = width / 4;
    const centerY = height / 4;

    // Clear canvas
    ctx.clearRect(0, 0, width / 2, height / 2);

    // Background
    ctx.fillStyle = 'rgba(6, 10, 18, 0.6)';
    ctx.fillRect(0, 0, width / 2, height / 2);

    if (viewMode === '3d') {
      // 3D Perspective view
      const draw3DBathymetry = () => {
        const gridSize = 20;
        const cellSize = 15;
        const baseY = height / 2 - 50;
        const perspectiveScale = 0.7;

        for (let row = 0; row < gridSize; row++) {
          for (let col = 0; col < gridSize; col++) {
            // Generate depth data (simulated seafloor)
            const noise1 = Math.sin(col * 0.3) * Math.cos(row * 0.2);
            const noise2 = Math.sin(col * 0.15 + row * 0.15);
            const depth = 2000 + noise1 * 500 + noise2 * 800 + Math.random() * 200;
            
            const x = centerX + (col - gridSize / 2) * cellSize * perspectiveScale;
            const y = baseY - (row * cellSize * perspectiveScale * 0.5) - (depth / 30);
            
            // Color based on depth
            const depthRatio = (depth - 1000) / 2500;
            const blue = Math.floor(55 + depthRatio * 100);
            const green = Math.floor(20 + (1 - depthRatio) * 100);
            
            ctx.fillStyle = `rgba(${blue}, ${green}, ${blue + 50}, 0.8)`;
            ctx.fillRect(x - cellSize / 2, y - cellSize / 2, cellSize, cellSize);

            // Draw contour lines
            if (showContours && depth % 500 < 100) {
              ctx.strokeStyle = 'rgba(55, 211, 255, 0.4)';
              ctx.lineWidth = 1;
              ctx.strokeRect(x - cellSize / 2, y - cellSize / 2, cellSize, cellSize);
            }
          }
        }
      };

      draw3DBathymetry();
    } else {
      // 2D Contour map
      const draw2DBathymetry = () => {
        const gridSize = 40;
        const cellSize = Math.min(width / 2, height / 2) / gridSize;

        for (let row = 0; row < gridSize; row++) {
          for (let col = 0; col < gridSize; col++) {
            const noise1 = Math.sin(col * 0.3) * Math.cos(row * 0.2);
            const noise2 = Math.sin(col * 0.15 + row * 0.15);
            const depth = 2000 + noise1 * 500 + noise2 * 800;
            
            const x = col * cellSize;
            const y = row * cellSize;
            
            const depthRatio = (depth - 1000) / 2500;
            const blue = Math.floor(55 + depthRatio * 100);
            const green = Math.floor(20 + (1 - depthRatio) * 100);
            
            ctx.fillStyle = `rgba(${blue}, ${green}, ${blue + 50}, 0.9)`;
            ctx.fillRect(x, y, cellSize, cellSize);
          }
        }

        // Draw contour lines
        if (showContours) {
          ctx.strokeStyle = 'rgba(55, 211, 255, 0.6)';
          ctx.lineWidth = 2;
          const contours = [1500, 2000, 2500, 3000, 3500];
          
          contours.forEach((contourDepth) => {
            ctx.beginPath();
            for (let angle = 0; angle < Math.PI * 2; angle += 0.1) {
              const radius = 80 + (contourDepth - 1500) / 20;
              const x = centerX + Math.cos(angle) * radius;
              const y = centerY + Math.sin(angle) * radius * 0.7;
              if (angle === 0) {
                ctx.moveTo(x, y);
              } else {
                ctx.lineTo(x, y);
              }
            }
            ctx.closePath();
            ctx.stroke();

            // Label
            ctx.fillStyle = 'rgba(55, 211, 255, 0.8)';
            ctx.font = '10px monospace';
            ctx.fillText(`${contourDepth}m`, centerX + 90 + (contourDepth - 1500) / 20, centerY);
          });
        }
      };

      draw2DBathymetry();
    }
  }, [viewMode, showContours]);

  return (
    <div 
      className="grid grid-cols-1 lg:grid-cols-[auto_1fr] lg:grid-rows-[56px_1fr] min-h-screen bg-background text-foreground"
      data-testid="bathymetry-page-container"
    >
      <Sidebar layers={layers} toggleLayer={toggleLayer} currentRoute="/bathymetry" />
      <Topbar vesselData={vesselData} />
      <main className="pt-14 lg:pt-0 lg:col-start-2 lg:row-start-2 p-4 md:p-6 relative z-0">
        <div className="mb-6" data-testid="page-header">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Mountain className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">3D Bathymetry Visualization</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" data-testid="export-bathymetry-btn">
                <Download className="h-4 w-4 mr-2" />
                Export Data
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">Interactive seafloor mapping with depth contours and geological features</p>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="bathymetry-page-grid">
          {/* Visualization Controls */}
          <Card className="col-span-12 xl:col-span-3" data-testid="viz-controls-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">View Controls</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-3">View Mode</h4>
                <div className="flex flex-col gap-2">
                  <Button 
                    variant={viewMode === '3d' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('3d')}
                    data-testid="view-mode-3d"
                    className="w-full justify-start"
                  >
                    <Layers className="h-4 w-4 mr-2" />
                    3D Perspective
                  </Button>
                  <Button 
                    variant={viewMode === '2d' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('2d')}
                    data-testid="view-mode-2d"
                    className="w-full justify-start"
                  >
                    <Mountain className="h-4 w-4 mr-2" />
                    2D Contour Map
                  </Button>
                </div>
              </div>

              <div className="pt-4 border-t border-border/40">
                <h4 className="text-sm font-medium mb-3">Display Options</h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm">Show Contours</span>
                    <input 
                      type="checkbox" 
                      checked={showContours}
                      onChange={(e) => setShowContours(e.target.checked)}
                      data-testid="toggle-contours"
                      className="w-4 h-4"
                    />
                  </label>
                </div>
              </div>

              <div className="pt-4 border-t border-border/40">
                <h4 className="text-sm font-medium mb-3">Depth Scale</h4>
                <div className="space-y-2">
                  {[
                    { color: 'rgb(55, 120, 155)', label: '1000-1500m' },
                    { color: 'rgb(75, 140, 175)', label: '1500-2000m' },
                    { color: 'rgb(95, 160, 195)', label: '2000-2500m' },
                    { color: 'rgb(115, 180, 215)', label: '2500-3000m' },
                    { color: 'rgb(135, 200, 235)', label: '3000-3500m' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs" data-testid={`depth-scale-${idx}`}>
                      <div className="w-6 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                      <span className="font-mono">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3D Visualization */}
          <div 
            className="col-span-12 xl:col-span-9 rounded-lg overflow-hidden border border-border/70 bg-[radial-gradient(100%_140%_at_50%_0%,rgba(55,211,255,0.06),transparent_60%),hsl(var(--card))] shadow-ambient"
            style={{ height: 'min(65vh, 800px)' }}
            data-testid="bathymetry-viz-container"
          >
            <div className="p-4 border-b border-border/40 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mountain className="h-4 w-4 text-primary" />
                <h3 className="font-medium">Seafloor Bathymetry</h3>
              </div>
              <Badge variant="outline" data-testid="viz-mode-badge">
                {viewMode === '3d' ? '3D Perspective' : '2D Contour'}
              </Badge>
            </div>
            <canvas 
              ref={bathymetryCanvasRef} 
              className="w-full h-full"
              data-testid="bathymetry-canvas"
              role="img"
              aria-label="3D bathymetry visualization showing seafloor depth and contours"
            />
          </div>

          {/* Statistics */}
          <Card className="col-span-12 lg:col-span-4" data-testid="bathymetry-stats-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ruler className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Survey Statistics</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Min Depth</span>
                  <span className="font-mono">1,245 m</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Max Depth</span>
                  <span className="font-mono">4,850 m</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Mean Depth</span>
                  <span className="font-mono">2,847 m</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Survey Area</span>
                  <span className="font-mono">487 km²</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Resolution</span>
                  <span className="font-mono">5 m</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Geological Features */}
          <Card className="col-span-12 lg:col-span-8" data-testid="geological-features-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mountain className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Detected Geological Features</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-secondary/20 border border-border/40" data-testid="feature-seamount">
                  <h4 className="font-medium mb-2">Seamount Ridge</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location</span>
                      <span className="font-mono">24.12°S, 135.45°W</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Peak Depth</span>
                      <span className="font-mono">1,340 m</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Height</span>
                      <span className="font-mono">2,180 m</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-secondary/20 border border-border/40" data-testid="feature-trench">
                  <h4 className="font-medium mb-2">Deep Trench</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location</span>
                      <span className="font-mono">24.89°S, 136.22°W</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Max Depth</span>
                      <span className="font-mono">4,850 m</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Length</span>
                      <span className="font-mono">47 km</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-secondary/20 border border-border/40" data-testid="feature-plateau">
                  <h4 className="font-medium mb-2">Abyssal Plateau</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location</span>
                      <span className="font-mono">23.45°S, 134.78°W</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg Depth</span>
                      <span className="font-mono">2,640 m</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Area</span>
                      <span className="font-mono">124 km²</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-secondary/20 border border-border/40" data-testid="feature-canyon">
                  <h4 className="font-medium mb-2">Submarine Canyon</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location</span>
                      <span className="font-mono">25.12°S, 136.90°W</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Depth Range</span>
                      <span className="font-mono">2,100-3,400 m</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Width</span>
                      <span className="font-mono">8.2 km</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default BathymetryPage;
